import { UploadService } from "@/services/APIs";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd/es/upload";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useField } from "@formily/react";

const isUUID = (val: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

export const CustomFileUpload = (
  props: UploadProps & {
    action?: string;
    value?: any;
    onChange?: (value: any) => void;
    defaultFileList?: File[];
    multiple?: boolean;
  }
) => {
  const { id } = useParams();
  const editMode = !!id;
  const { value, onChange, multiple = false, ...restProps } = props;
  // Get direct access to Formily field to set value reliably
  const field = useField<any>();

  // uploadedFiles is our single source of truth for what to DISPLAY
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  // Track whether user has manually uploaded something in this session
  const userUploadedRef = useRef(false);
  const lastValueRef = useRef<string>("");

  useEffect(() => {
    // Once user has uploaded a file, don't let the value prop override it
    // (because value prop lags behind until next render cycle)
    if (userUploadedRef.current) {
      return;
    }

    // Normalize the incoming value
    let raw = value;

    // If it's already an empty array or falsy, clear the display
    if (!raw || (Array.isArray(raw) && raw.length === 0)) {
      if (uploadedFiles.length > 0 && !userUploadedRef.current) {
        setUploadedFiles([]);
      }
      return;
    }

    // Parse string value
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        // plain string or UUID — keep as-is
      }
    }

    // Normalize to array
    if (!Array.isArray(raw)) {
      raw = [raw];
    }

    const valueStr = JSON.stringify(raw);
    if (valueStr === lastValueRef.current) {
      return;
    } // no change
    lastValueRef.current = valueStr;

    // Resolve UUIDs to file objects
    const resolve = async () => {
      const resolved = await Promise.all(
        raw.map(async (item: any) => {
          if (typeof item === "string" && isUUID(item)) {
            try {
              const fileData = await UploadService.getFileById({ id: item });
              return fileData;
            } catch {
              return null;
            }
          }
          return item;
        })
      );
      const valid = resolved.filter(Boolean);
      if (valid.length > 0) {
        setUploadedFiles(valid);
      }
    };

    resolve();
  }, [value, editMode, uploadedFiles]); // intentionally NOT including uploadedFiles

  const handleFileSelect = async (file: File) => {
    try {
      const response = await UploadService.uploadFiles({
        folderName: "uploads",
        files: [file],
      });

      console.log("[CustomFileUpload] upload API response:", response);

      // Normalize response to an array of items (strings or objects)
      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response && typeof response === "object") {
        items = [response]; // single object response
      } else if (typeof response === "string") {
        items = [response]; // plain string URL
      }

      if (items.length > 0) {
        // Extract string URLs or keep objects as-is
        const existingUrls = uploadedFiles.map((f: any) =>
          typeof f === "string" ? f : (f?.file_path ?? f?.url ?? f?.id ?? "")
        );

        const uniqueNew = items.filter((item: any) => {
          const key =
            typeof item === "string"
              ? item
              : (item?.file_path ?? item?.url ?? item?.id ?? "");
          return !existingUrls.includes(key);
        });

        const newValueObjects = multiple
          ? [...uploadedFiles, ...uniqueNew]
          : uniqueNew; // single file mode: replace

        // Extract ONLY IDs for the form value
        const newValueIds = newValueObjects.map((f: any) =>
          typeof f === "string" ? f : (f?.id ?? f?.file_path ?? f?.url ?? "")
        );

        userUploadedRef.current = true;
        setUploadedFiles(newValueObjects);

        // Use both to ensure compatibility
        onChange?.(newValueIds);
        if (field) {
          field.setValue(newValueIds);
        }
      }
    } catch (error) {
      console.error("[CustomFileUpload] Upload failed:", error);
    }
    return false;
  };

  const handleRemove = (file: any) => {
    const fileUrl = file.url ?? file.file_path ?? file.name ?? "";

    const filteredFiles = uploadedFiles.filter((f: any) => {
      const fUrl =
        typeof f === "string" ? f : (f?.file_path ?? f?.url ?? f?.name ?? "");
      return fUrl !== fileUrl;
    });

    userUploadedRef.current = filteredFiles.length > 0;
    setUploadedFiles(filteredFiles);

    const filteredIds = filteredFiles.map((f: any) =>
      typeof f === "string" ? f : (f?.id ?? f?.file_path ?? f?.url ?? "")
    );

    onChange?.(filteredIds);
    if (field) {
      field.setValue(filteredIds);
    }
  };

  const handleChange = (info: any) => {
    if (info.file.status === "removed") {
      handleRemove(info.file);
    }
  };

  const handlePreview = (file: any) => {
    let url = file.url ?? file.file_path;
    if (!url) {
      return;
    }

    if (url.startsWith("http://djangoproject.universal-software.in")) {
      url = url.replace("http://", "https://");
    }
    window.open(url, "_blank");
  };

  const fileList: UploadFile[] = (uploadedFiles ?? []).map(
    (file: any, index: number) => {
      // Handle plain string URL (returned by upload API)
      if (typeof file === "string") {
        const filename = file.split("/").pop() ?? "file";
        return {
          uid: `rc-upload-${index}`,
          name: filename,
          status: "done" as const,
          url: file,
        };
      }

      // Handle file object from backend {id, file_path, file_original_name, ...}
      if (typeof file === "object" && (file?.file_path || file?.url)) {
        return {
          uid: file?.id ?? `rc-upload-${index}`,
          name: file?.file_original_name ?? file?.name ?? "file",
          status: "done" as const,
          url: file?.file_path ?? file?.url,
        };
      }

      return {
        uid: `rc-upload-${index}`,
        name: "file",
        status: "done" as const,
        url: "",
      };
    }
  );

  const showUploadButton =
    multiple || !uploadedFiles || uploadedFiles.length === 0;

  return (
    <div className="space-y-2">
      <Upload
        {...restProps}
        beforeUpload={handleFileSelect}
        onChange={handleChange}
        onRemove={handleRemove}
        maxCount={multiple ? undefined : 1}
        multiple={multiple}
        fileList={fileList}
        showUploadList={{
          showRemoveIcon: true,
          showDownloadIcon: true,
          showPreviewIcon: true,
          downloadIcon: <DownloadOutlined />,
          removeIcon: <DeleteOutlined />,
          previewIcon: <EyeOutlined />,
        }}
        onPreview={handlePreview}
      >
        {showUploadButton && (
          <Button icon={<UploadOutlined />}>
            {multiple ? "Upload Files" : "Upload File"}
          </Button>
        )}
      </Upload>
    </div>
  );
};
